import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';
import { NavLink } from 'react-router-dom';

const resetScrollPosition = () => window.scrollTo(0, 0);

/**
 * @typedef NavButtonProps
 * @property {string} text The text to display on the button
 * @property {string} icon The path to the icon to display on the button
 * @property {string} target The target URL
 * @property {boolean} exact Whether the target URL should be matched exactly
 */

/**
 * A NavButton component that renders a NavLink
 *
 * @param {NavButtonProps} props NavButton props
 * @returns {JSX.Element} NavButton component
 */
const NavButton = ({ text = '', icon = null, target = '/', exact = true }) => (
  <li>
    <NavLink onClick={resetScrollPosition} activeClassName="is-active" exact={exact} to={target}>
      {icon && <FontAwesomeIcon icon={icon.replace('fa-', '').split(' ')} />}
      {i18n.translate(`nav.${text}`)}
    </NavLink>
  </li>
);

/** 
 * @typedef ButtonSetProps
 * @property {Array<NavButtonProps>} buttons The buttons to render
 * @property {string} orientation The orientation of the buttons (left or right)
 */

/**
 * Renders a set of buttons (NavLinks)
 * 
 * @param {ButtonSetProps} props ButtonSetProps
 * @returns {JSX.Element} ButtonSet component
 */
const ButtonSet = ({buttons = {}, orientation = "left", showBubbles=false}) => {

  const buttonElements = Object.values(buttons).map((button) => (
    <NavButton key={button.text} {...button} />
  ));

  const verticalOrientations = ['left', 'right'];
  const horizontalOrientations = ['top', 'bottom'];

  const ulClasses = clsx('button-set', {
    'button-set--horizontal': horizontalOrientations.includes(orientation),
    'button-set--vertical': verticalOrientations.includes(orientation),
  });

  return (
    <ul className={ulClasses}>
      {buttonElements}
      {showBubbles && (
        <React.Fragment>
          <div className="button-set__decoration button-set__decoration--bubble-1" />
          <div className="button-set__decoration button-set__decoration--bubble-2" />
          <div className="button-set__decoration button-set__decoration--bubble-3" />
          <div className="button-set__decoration button-set__decoration--bubble-4" />
          <div className="button-set__decoration button-set__decoration--bubble-5" />
          <div className="button-set__decoration button-set__decoration--bubble-6" />
        </React.Fragment>
      )}
    </ul>
  );
}

export default ButtonSet;

NavButton.propTypes = {
  text: PropTypes.string,
  icon: PropTypes.string,
  target: PropTypes.string,
  exact: PropTypes.bool,
};

ButtonSet.propTypes = {
  buttons: PropTypes.arrayOf(PropTypes.shape()),
  orientation: PropTypes.string,
  showBubbles: PropTypes.bool,
};
